import React, { useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { ColDef, RowValueChangedEvent, SideBarDef } from 'ag-grid-enterprise'
import { AgGridReact } from 'ag-grid-react'
import { Loan } from '../types/Loan'
import { LoanSummaryLoan } from '../types/LoanSummary'
import { Purpose } from '../../../types/purpose/Purpose'
import { Investment } from '../../../types/investment/Investment'
import { toFormDate } from '../../../config/dayjsConfig'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { filtersSelector } from '../../../redux/context/filters/selectors'
import { useUpdateLoan } from '../queries/useUpdateLoan'
import { useDeleteLoan } from '../queries/useDeleteLoan'
import { useDeleteInvestment } from '../../investment/hooks/useDeleteInvestment'
import { fetchLoan } from '../service/loanService'
import loanQueryKeys from '../queries/loanQueryKeys'
import CustomAgGrid from '../../../shared/components/custom/Grid/CustomAgGrid'
import { useLoanColumns } from '../hooks/useLoanColumns'
import { getStatusBarConfig } from '../../../shared/utils/agGridConfig'
import { showSuccessMessage, showErrorMessage } from '../../../utils/message'
import './LoanGrid.css'

const sideBar: SideBarDef = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true
      }
    }
  ],
  defaultToolPanel: ''
}

interface LoanGridProps {
  loans: Loan[]
  isExisting: boolean
  balanceSheetRowId: number
  toggleRepaymentSchedule: (e: React.MouseEvent<HTMLElement, MouseEvent>, loan: Loan) => void
  toggleInterestRate: (e: React.MouseEvent<HTMLElement, MouseEvent>, loan: Loan) => void
  toggleModal: (investment?: Investment) => void
}

const LoanGrid: React.FC<LoanGridProps> = ({
  loans,
  isExisting,
  balanceSheetRowId,
  toggleRepaymentSchedule,
  toggleInterestRate,
  toggleModal
}) => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const companyId = useSelector(contextCompanyIdSelector)!
  const { budgetingScenario } = useSelector(filtersSelector)
  const updateLoan = useUpdateLoan()
  const deleteLoan = useDeleteLoan()
  const deleteInvestment = useDeleteInvestment(isExisting)
  const queryClient = useQueryClient()
  const gridRef = useRef<AgGridReact>(null)
  const editingRowIdRef = useRef<number | null>(null)
  const originalRowDataRef = useRef<LoanSummaryLoan | null>(null)

  const loanData = useMemo(() => loans.slice().sort((a, b) => a.id - b.id), [loans])

  const onDelete = useCallback(
    async (loan: Loan) => {
      try {
        if (isExisting) {
          await deleteLoan.mutateAsync(loan.id)
          message.success(t('messages:deleteSuccess', 'Success'))
          queryClient.invalidateQueries({
            queryKey: loanQueryKeys.summaries()
          })
        } else {
          const data = await fetchLoan(companyId, loan.id)
          if (data.investment) {
            await deleteInvestment.mutateAsync(data.investment, {
              onSuccess: () => {
                queryClient.invalidateQueries({
                  queryKey: loanQueryKeys.summaries()
                })
              }
            })
          }
        }
      } catch {
        message.error('Failed deleting Loan')
      }
    },
    [isExisting, companyId, deleteLoan, deleteInvestment, queryClient, message]
  )

  const onStartEdit = useCallback((loan: Loan) => {
    editingRowIdRef.current = loan.id
    const rowNode = gridRef.current?.api.getRowNode(String(loan.id))
    originalRowDataRef.current = rowNode?.data ? structuredClone(rowNode.data) : null
    if (rowNode?.rowIndex != null) {
      gridRef.current?.api.setFocusedCell(rowNode.rowIndex, 'purposes')
      gridRef.current?.api.startEditingCell({
        rowIndex: rowNode.rowIndex,
        colKey: 'purposes'
      })
    }
    setTimeout(() => {
      gridRef.current?.api?.refreshCells({ columns: ['actions'], force: true })
    }, 0)
  }, [])

  const onEditClick = useCallback(
    async (loan: Loan) => {
      try {
        const data = await fetchLoan(companyId, loan.id)
        if (data.investment) {
          toggleModal(data.investment)
        }
      } catch {
        message.error('Failed loading Investment')
      }
    },
    [companyId, budgetingScenario, toggleModal, message]
  )

  const onSaveEdit = useCallback(() => {
    gridRef.current?.api.stopEditing(false)
  }, [])

  const onCancelEdit = useCallback(() => {
    const id = editingRowIdRef.current
    gridRef.current?.api.stopEditing(true)
    if (originalRowDataRef.current !== null && id !== null) {
      const rowNode = gridRef.current?.api.getRowNode(String(id))
      rowNode?.setData(originalRowDataRef.current)
    }
    originalRowDataRef.current = null
  }, [])

  const { columnDefs } = useLoanColumns({
    loans,
    isExisting,
    isLocked: budgetingScenario?.isLocked === true,
    balanceSheetRowId,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onDelete,
    onEditClick,
    toggleRepaymentSchedule,
    toggleInterestRate
  })

  const gridContext = useMemo(() => ({ editingRowIdRef }), [])

  const onRowValueChanged = useCallback(
    (event: RowValueChangedEvent<LoanSummaryLoan>) => {
      if (!event.data) return

      const { summary, ...saveData } = event.data

      const values = {
        ...saveData,
        nextInstalmentDate: toFormDate(saveData.nextInstalmentDate) as string,
        purposes: saveData.purposes as Purpose[],
        interestRates: saveData.interestRates?.map((r: any) => ({
          ...r,
          date: toFormDate(r.date)
        }))
      }

      updateLoan
        .mutateAsync(values)
        .then(() => {
          showSuccessMessage()
          queryClient.invalidateQueries({
            queryKey: loanQueryKeys.summaries()
          })
          queryClient.invalidateQueries({
            queryKey: [{ scope: 'loan', entity: 'instalments' }]
          })
        })
        .catch(() => {
          showErrorMessage()
        })
    },
    [updateLoan, queryClient]
  )

  const onRowEditingStopped = useCallback(() => {
    editingRowIdRef.current = null
    gridRef.current?.api?.refreshCells({ columns: ['actions'], force: true })
  }, [])

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      minWidth: 60,
      suppressHeaderMenuButton: true,
      cellDataType: false,
      suppressKeyboardEvent: params => {
        if (params.editing) return params.event.key === 'Enter' || params.event.key === 'Escape'
        const { key } = params.event
        return key === 'Enter' || key === 'F2' || key === 'Backspace' || key === 'Delete' || key.length === 1
      }
    }),
    []
  )

  return (
    <div className={`loan-grid ${loanData.length > 20 ? 'loan-grid--fixed' : ''}`}>
      <CustomAgGrid
        ref={gridRef}
        rowData={loanData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        context={gridContext}
        sideBar={sideBar}
        statusBar={getStatusBarConfig()}
        cellSelection
        suppressClipboardPaste
        editType="fullRow"
        suppressClickEdit
        popupParent={document.body}
        onRowValueChanged={onRowValueChanged}
        onRowEditingStopped={onRowEditingStopped}
        getRowId={p => String(p.data.id)}
        domLayout={loanData.length > 20 ? 'normal' : 'autoHeight'}
      />
    </div>
  )
}

export default LoanGrid
